import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Container,
  Snackbar,
  SnackbarCloseReason,
  Tab,
  Tabs,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { BusinessBaseStatisticsTab } from './BusinessBaseStatisticsTab';
import MostRequestedTab from './tabs/MostRequestedTab';
import MostSearchedTab from './tabs/MostSearchedTab';

const BusinessBaseStatistics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams();
  const [snackbarError, setSnackbarError] = useState({
    open: false,
    message: '',
  });

  const handleFetchError = useCallback((message: string) => {
    setSnackbarError({ open: true, message });
  }, []);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarError({
      open: false,
      message: snackbarError.message,
    });
  };

  if (
    !Object.values(BusinessBaseStatisticsTab).includes(
      tab as BusinessBaseStatisticsTab
    )
  ) {
    return <Navigate to={`../${BusinessBaseStatisticsTab.MostSearched}`} replace />;
  }
  const activeTab = tab as BusinessBaseStatisticsTab;

  return (
    <>
      <Box
        sx={{
          maxHeight: 'calc(100vh - 12rem)',
          overflowY: 'auto',
          backgroundColor: sgrColors.frost_grey,
        }}
      >
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            paddingY: '1.5rem',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue: BusinessBaseStatisticsTab) =>
              navigate(`../${newValue}`)
            }
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: '3rem',
              borderBottom: `1px solid ${sgrColors.grey_cloud}`,
              '& .MuiTabs-indicator': {
                height: '3px',
                borderRadius: '3px 3px 0 0',
                backgroundColor: sgrColors.navy,
              },
              '& .MuiTab-root': {
                minHeight: '3rem',
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: sgrColors.backdrop,
                '&:hover': { color: sgrColors.navy },
                '&.Mui-selected': { color: sgrColors.navy, fontWeight: 600 },
              },
            }}
          >
            <Tab
              value={BusinessBaseStatisticsTab.MostSearched}
              icon={<LeaderboardOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t('business_base_statistics.most_searched_tab')}
            />
            <Tab
              value={BusinessBaseStatisticsTab.MostRequested}
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t('business_base_statistics.most_requested_tab')}
            />
          </Tabs>

          {activeTab === BusinessBaseStatisticsTab.MostSearched && (
            <MostSearchedTab onFetchError={handleFetchError} />
          )}
          {activeTab === BusinessBaseStatisticsTab.MostRequested && (
            <MostRequestedTab onFetchError={handleFetchError} />
          )}
        </Container>
      </Box>
      <Snackbar
        open={snackbarError.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{
            borderRadius: '0.5rem',
            backgroundColor: sgrColors.rose,
            color: '#ffffff',
          }}
        >
          {snackbarError.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BusinessBaseStatistics;
